// src/client/buddha.ts — Q 版佛像 SVG 圖庫（3 種畫風 × 四態）
// States are driven by a wrapper class on the stage element:
//   .state-idle | .state-focus | .state-break | .state-complete
// Add .buddha-live to enable breathing animation.
import type { BuddhaStyle } from "./types";

const BUDDHA_CSS = `
.buddha .halo, .buddha .glow, .buddha .tea { opacity: 0; transition: opacity 0.8s ease; }
.buddha .eyes-open, .buddha .mouth-smile { opacity: 0; transition: opacity 0.3s ease; }
.buddha .eyes-closed, .buddha .mouth-calm { opacity: 1; transition: opacity 0.3s ease; }

.state-break .eyes-open, .state-break .mouth-smile { opacity: 1; }
.state-break .eyes-closed, .state-break .mouth-calm { opacity: 0; }
.state-break .tea { opacity: 1; }

.state-focus .halo { opacity: calc(0.2 + 0.8 * var(--anim, 1)); }

.buddha .halo-rays { transform-origin: 100px 72px; }
.state-focus .halo-rays { animation: bd-halo-spin 18s linear infinite; }
@keyframes bd-halo-spin { to { transform: rotate(360deg); } }

.buddha .breath { transform-origin: 100px 168px; }
.buddha-live .breath { animation: bd-breath 4.8s ease-in-out infinite; }
@keyframes bd-breath {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(calc(1 + 0.02 * var(--anim, 1))); }
}

.buddha .steam-line { opacity: 0; }
.state-break .steam-line { animation: bd-steam 2.6s ease-in-out infinite; }
.state-break .steam-line.s2 { animation-delay: 1.1s; }
@keyframes bd-steam {
  0%   { transform: translateY(0); opacity: 0; }
  30%  { opacity: calc(0.7 * var(--anim, 1)); }
  100% { transform: translateY(-10px); opacity: 0; }
}

.buddha .glow { filter: blur(16px); }
.state-complete .glow { animation: bd-glow 1.8s ease-out; }
@keyframes bd-glow {
  0% { opacity: 0; } 25% { opacity: 0.55; } 100% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .state-focus .halo-rays, .buddha-live .breath, .state-break .steam-line { animation: none; }
  .state-break .steam-line { opacity: 0.4; }
}
`;

/* shared bits ------------------------------------------------ */
const halo = (inner: string = ""): string => `
  <g class="halo">
    <circle cx="100" cy="72" r="49" fill="none" stroke="var(--gold)" stroke-width="2.5"
            stroke-dasharray="270 38" stroke-linecap="round" opacity="0.9"></circle>
    <g class="halo-rays" stroke="var(--gold)" stroke-width="2" stroke-linecap="round" opacity="0.75">
      <line x1="100" y1="14" x2="100" y2="6"></line>
      <line x1="141" y1="31" x2="147" y2="25"></line>
      <line x1="158" y1="72" x2="166" y2="72"></line>
      <line x1="141" y1="113" x2="147" y2="119"></line>
      <line x1="59" y1="31" x2="53" y2="25"></line>
      <line x1="42" y1="72" x2="34" y2="72"></line>
      <line x1="59" y1="113" x2="53" y2="119"></line>
    </g>
    ${inner}
  </g>`;

const glow = `<circle class="glow" cx="100" cy="100" r="66" fill="var(--gold)" opacity="0"></circle>`;

const face = (ink: string, sw: number): string => `
  <g class="eyes-closed" stroke="${ink}" stroke-width="${sw}" stroke-linecap="round" fill="none">
    <path d="M84,82 q5,4 11,0"></path>
    <path d="M105,82 q5,4 11,0"></path>
  </g>
  <g class="eyes-open" fill="${ink}">
    <circle cx="89.5" cy="82" r="2.4"></circle>
    <circle cx="110.5" cy="82" r="2.4"></circle>
  </g>
  <path class="mouth-calm" d="M95,95 q5,3.5 10,0" stroke="${ink}" stroke-width="${sw}" stroke-linecap="round" fill="none"></path>
  <path class="mouth-smile" d="M92,93 q8,7 16,0" stroke="${ink}" stroke-width="${sw}" stroke-linecap="round" fill="none"></path>`;

const tea = (cup: string, ink: string, sw: number): string => `
  <g class="tea">
    <path class="steam-line" d="M94,124 q-3,-5 0,-9 q3,-4 0,-8" stroke="${ink}" stroke-width="1.8" stroke-linecap="round" fill="none"></path>
    <path class="steam-line s2" d="M106,124 q3,-5 0,-9 q-3,-4 0,-8" stroke="${ink}" stroke-width="1.8" stroke-linecap="round" fill="none"></path>
    <path d="M86,128 h28 l-4,13 h-20 Z" fill="${cup}" stroke="${ink}" stroke-width="${sw}" stroke-linejoin="round"></path>
    <line x1="82" y1="143" x2="118" y2="143" stroke="${ink}" stroke-width="${sw}" stroke-linecap="round"></line>
  </g>`;

/* ① 墨筆白描 — minimal ink line art ---------------------------- */
function inkBuddha(): string {
  const ink = "var(--ink)";
  return `<svg class="buddha buddha-ink" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" aria-label="佛像">
    ${glow}
    ${halo()}
    <g class="breath">
      <g fill="var(--paper)" stroke="${ink}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <ellipse cx="100" cy="161" rx="43" ry="15"></ellipse>
        <path d="M70,158 C65,122 79,106 100,106 C121,106 135,122 130,158"></path>
        <path d="M66,70 C59,72 59,90 66,92"></path>
        <path d="M134,70 C141,72 141,90 134,92"></path>
        <circle cx="100" cy="78" r="33"></circle>
        <circle cx="100" cy="43" r="7"></circle>
        <ellipse cx="100" cy="146" rx="14" ry="7.5"></ellipse>
      </g>
      ${face(ink, 2.6)}
      ${tea("var(--paper)", ink, 2.6)}
    </g>
  </svg>`;
}

/* ② 圓潤貼紙 — chubby sticker style ---------------------------- */
function stickerBuddha(): string {
  const ink = "#4A3B2E";
  const skin = "#F6DCB8";
  return `<svg class="buddha buddha-sticker" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" aria-label="佛像">
    ${glow}
    ${halo(`<circle cx="100" cy="20" r="3" fill="var(--gold)"></circle>`)}
    <g class="breath">
      <path d="M58,166 Q53,116 100,110 Q147,116 142,166 Q100,181 58,166 Z"
            fill="var(--cinnabar)" stroke="${ink}" stroke-width="3.2" stroke-linejoin="round"></path>
      <path d="M85,114 L100,133 L115,114" fill="none" stroke="#F2E4C8" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"></path>
      <ellipse cx="68" cy="88" rx="5.5" ry="10" fill="${skin}" stroke="${ink}" stroke-width="3"></ellipse>
      <ellipse cx="132" cy="88" rx="5.5" ry="10" fill="${skin}" stroke="${ink}" stroke-width="3"></ellipse>
      <circle cx="100" cy="78" r="37" fill="${skin}" stroke="${ink}" stroke-width="3.2"></circle>
      <path d="M63,72 Q67,46 100,42 Q133,46 137,72 Q120,58 100,58 Q80,58 63,72 Z"
            fill="#54616B" stroke="${ink}" stroke-width="3" stroke-linejoin="round"></path>
      <circle cx="100" cy="41" r="7.5" fill="#54616B" stroke="${ink}" stroke-width="3"></circle>
      <ellipse cx="79" cy="93" rx="6" ry="3.6" fill="#EFA987" opacity="0.8"></ellipse>
      <ellipse cx="121" cy="93" rx="6" ry="3.6" fill="#EFA987" opacity="0.8"></ellipse>
      <circle cx="100" cy="149" r="11" fill="${skin}" stroke="${ink}" stroke-width="3"></circle>
      ${face(ink, 3)}
      ${tea("#9FB9C4", ink, 3)}
    </g>
  </svg>`;
}

/* ③ 剪紙平塗 — flat papercut style ------------------------------ */
function papercutBuddha(): string {
  const ink = "#3E4435";
  const skin = "#EAD9BD";
  return `<svg class="buddha buddha-papercut" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" aria-label="佛像">
    ${glow}
    <g class="halo">
      <circle cx="100" cy="72" r="50" fill="var(--gold)" opacity="0.28"></circle>
      <circle cx="100" cy="72" r="42" fill="var(--gold)" opacity="0.3"></circle>
      <g class="halo-rays" fill="var(--gold)" opacity="0.85">
        <circle cx="100" cy="16" r="3"></circle>
        <circle cx="148" cy="38" r="3"></circle>
        <circle cx="158" cy="90" r="3"></circle>
        <circle cx="52" cy="38" r="3"></circle>
        <circle cx="42" cy="90" r="3"></circle>
      </g>
    </g>
    <g class="breath">
      <ellipse cx="100" cy="165" rx="46" ry="13" fill="#5C6B51"></ellipse>
      <path d="M62,168 L74,112 Q100,101 126,112 L138,168 Q100,180 62,168 Z" fill="var(--matcha)"></path>
      <path d="M74,128 Q100,138 126,128 L128,140 Q100,150 72,140 Z" fill="var(--gold)" opacity="0.9"></path>
      <ellipse cx="70" cy="86" rx="5" ry="9.5" fill="${skin}"></ellipse>
      <ellipse cx="130" cy="86" rx="5" ry="9.5" fill="${skin}"></ellipse>
      <circle cx="100" cy="76" r="32" fill="${skin}"></circle>
      <path d="M68,70 Q72,47 100,44 Q128,47 132,70 Q116,59 100,59 Q84,59 68,70 Z" fill="${ink}"></path>
      <circle cx="100" cy="44" r="6.5" fill="${ink}"></circle>
      <circle cx="100" cy="150" r="9" fill="${skin}"></circle>
      ${face(ink, 2.4)}
      ${tea("#C9B690", ink, 2.4)}
    </g>
  </svg>`;
}

/* 木魚 ---------------------------------------------------------- */
function woodfishSVG(): string {
  return `<svg viewBox="0 0 120 104" xmlns="http://www.w3.org/2000/svg" aria-label="木魚">
    <g class="wf-mallet" transform="rotate(18 96 26)">
      <line x1="96" y1="26" x2="118" y2="4" stroke="#6E4A2E" stroke-width="5" stroke-linecap="round"></line>
      <ellipse cx="94" cy="28" rx="9" ry="7" fill="#8B5E3C" stroke="#6E4A2E" stroke-width="2.5"></ellipse>
    </g>
    <path d="M60,92 C28,92 13,69 17,48 C21,28 40,18 60,18 C80,18 99,28 103,48 C107,69 92,92 60,92 Z"
          fill="#A4714B" stroke="#6E4A2E" stroke-width="3.5" stroke-linejoin="round"></path>
    <path d="M30,62 Q60,78 90,62" fill="none" stroke="#5C3D24" stroke-width="6" stroke-linecap="round"></path>
    <path d="M40,42 a6,6 0 1 1 6,6" fill="none" stroke="#6E4A2E" stroke-width="2.5" stroke-linecap="round"></path>
    <path d="M74,42 a6,6 0 1 1 6,6" fill="none" stroke="#6E4A2E" stroke-width="2.5" stroke-linecap="round"></path>
  </svg>`;
}

interface BuddhaStyleDef {
  id: BuddhaStyle;
  name: string;
  render: () => string;
}

const STYLES: BuddhaStyleDef[] = [
  { id: "ink", name: "墨筆白描", render: inkBuddha },
  { id: "sticker", name: "圓潤貼紙", render: stickerBuddha },
  { id: "papercut", name: "剪紙平塗", render: papercutBuddha },
];

export const Buddha = {
  css: BUDDHA_CSS,
  styles: STYLES,
  svg(id: BuddhaStyle | string): string {
    const st = STYLES.find((s) => s.id === id) ?? STYLES[0];
    return st.render();
  },
  woodfishSVG,
  inject(): void {
    if (document.getElementById("buddha-css")) return;
    const s = document.createElement("style");
    s.id = "buddha-css";
    s.textContent = BUDDHA_CSS;
    document.head.appendChild(s);
  },
};
